import * as React from "react";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { Media } from "src/typings/microserviceModels/content-flex-module";
import { UitkGalleryCarousel } from "uitk-react-gallery-carousel";
import { BlossomImage } from "src/components/shared/BlossomImage/BlossomImage";
import { GalleryImage } from "src/components/shared/Gallery/typings";
import {
  Action,
  FlexTrackingInfo,
  sendImmediateClickstreamTrackEvent,
} from "src/components/utility/analytics/FlexAnalyticsUtils";
import { QueryRoute, AnalyticsStore, RouteStore } from "bernie-core";
import { Layer } from "bernie-view";
import { UitkToolbar } from "uitk-react-toolbar";
import { useLocalization } from "@shared-ui/localization-context";
import {
  UitkMediaGalleryImageDialog,
  UitkMediaGalleryImageView,
  UitkMediaGalleryThumbnailView,
} from "uitk-react-media-gallery";
import { UitkFullscreenDialog, UitkDialogContent, UitkDialogAnimationType } from "uitk-react-dialog";
import { getFullScreenImages } from "src/components/utility/GalleryImageUtil";
import LazyLoad from "react-lazyload";
import { UitkLayoutPosition } from "uitk-react-layout-position";
import { UitkCardPlaceholder } from "uitk-react-cards";
import { withStores } from "src/stores";
import { observer } from "mobx-react";
import { useClickTracker } from "@shared-ui/clickstream-analytics-context";

interface MediaCarouselProps {
  id: string;
  media: Media[];
  title: string;
  identifier?: string;
  analytics?: AnalyticsStore;
  routeStore: RouteStore;
}

export const VideoDisplay = ({ id, video }: { id: string; video: Media }) => {
  const embedUrl = video.mediaUrl.split("watch?v=").join("embed/");

  return (
    <LazyLoad offset={500} height="100%" once placeholder={<UitkCardPlaceholder data-testid="editorial-video" />}>
      <UitkLayoutPosition type="relative" className="contentVideoContainer" tag="div">
        <UitkLayoutPosition type="absolute" position={{ top: "zero", left: "zero" }} cloneElement>
          <iframe
            id={`${id}-video`}
            title={video.mediaAlt}
            src={embedUrl}
            frameBorder="0"
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            height="100%"
            width="100%"
          />
        </UitkLayoutPosition>
      </UitkLayoutPosition>
    </LazyLoad>
  );
};

export const MediaCarousel = withStores("routeStore")(
  observer((props: MediaCarouselProps) => {
    const { id, media, title, identifier, routeStore } = props;
    const convertedImages = media.reduce((acc: GalleryImage[], item) => {
      const { mediaUrl, mediaAlt, mediaCaption } = item;
      acc.push({
        alt: mediaAlt,
        src: mediaUrl,
        thumbnailSrc: mediaUrl,
        caption: mediaCaption,
      });

      return acc;
    }, []);

    const [currentCarouselIndex, setCurrentCarouselIndex] = React.useState(0);
    const [currentMediaGalleryIndex, setCurrentMediaGalleryIndex] = React.useState(0);
    /* istanbul ignore next */
    const mediaGalleryQuery = identifier ? { gallery: `open${identifier}` } : { gallery: "open" };
    /* istanbul ignore next */
    const thumbnailsGalleryQuery = identifier ? { thumbnails: `open${identifier}` } : { thumbnails: "open" };
    const galleryImages = getFullScreenImages(convertedImages);
    const { formatText } = useLocalization();
    const track = useClickTracker();

    const setCarouselIndex = (index: number) => {
      /* istanbul ignore next */
      const trackingInfo: FlexTrackingInfo = {
        moduleName: "GalleryCarousel",
        rfrr: "carousel.gallery-nav.btn",
        action: Action.CLICK,
        linkName: "Navigating carousel gallery viewer",
      };
      /* istanbul ignore next */
      sendImmediateClickstreamTrackEvent(trackingInfo, track);
      setCurrentCarouselIndex(index);
    };

    const openMediaGallery = (index?: number) => {
      const trackingInfo: FlexTrackingInfo = {
        moduleName: "GalleryCarousel",
        rfrr: "carousel.gallery.open",
        action: Action.CLICK,
        linkName: "RFRR Action Event",
      };
      sendImmediateClickstreamTrackEvent(trackingInfo, track);
      index === undefined ? setCurrentMediaGalleryIndex(currentCarouselIndex) : setCurrentMediaGalleryIndex(index);
      routeStore?.updateSearch(mediaGalleryQuery);
    };

    const openThumbnailsGallery = () => {
      const trackingInfo: FlexTrackingInfo = {
        moduleName: "GalleryCarouselThumbnails",
        rfrr: "carousel.gallery.thumbnails.open",
        action: Action.CLICK,
        linkName: "RFRR Action Event",
      };
      sendImmediateClickstreamTrackEvent(trackingInfo, track);
      routeStore?.updateSearch(thumbnailsGalleryQuery);
    };

    const closeMediaGallery = () => {
      const trackingInfo: FlexTrackingInfo = {
        moduleName: "GalleryCarousel",
        rfrr: "carousel.gallery.close",
        action: Action.CLICK,
        linkName: "RFRR Action Event",
      };
      sendImmediateClickstreamTrackEvent(trackingInfo, track);
      routeStore.closeDialog("gallery");
    };

    const closeThumbnailsGallery = () => {
      const trackingInfo: FlexTrackingInfo = {
        moduleName: "GalleryCarouselThumbnails",
        rfrr: "carousel.gallery.thumbnails.close",
        action: Action.CLICK,
        linkName: "RFRR Action Event",
      };
      sendImmediateClickstreamTrackEvent(trackingInfo, track);
      routeStore.closeDialog("thumbnails");
      document.getElementById("galleryCarouselOpenButton")?.focus();
    };

    return (
      <div data-testid="galleryCarousel">
        <UitkGalleryCarousel
          images={convertedImages}
          carouselId={id}
          carouselName="Editorial Media Gallery Carousel"
          allyHeadingText="Media Gallery Carousel"
          currentIndex={currentCarouselIndex}
          onIndexChange={setCarouselIndex}
          onImageClick={openMediaGallery}
          onMediaButtonClick={openThumbnailsGallery}
          showGalleryButton={true}
        />
        <QueryRoute query={mediaGalleryQuery}>
          <Layer id="media-gallery" onDismiss={closeMediaGallery}>
            <UitkMediaGalleryImageDialog
              animation={UitkDialogAnimationType.NONE}
              dialogShow
              strictFullscreen
              onDismiss={closeMediaGallery}
            >
              <UitkMediaGalleryImageView
                images={galleryImages}
                currentIndex={currentMediaGalleryIndex}
                onIndexChange={setCurrentMediaGalleryIndex}
              />
            </UitkMediaGalleryImageDialog>
          </Layer>
        </QueryRoute>
        <QueryRoute query={thumbnailsGalleryQuery}>
          <Layer id="media-gallery-thumbnails" onDismiss={closeThumbnailsGallery} data-testid="media-thumbnails">
            <UitkFullscreenDialog
              strictFullscreen={false}
              dialogShow={false}
              animation={UitkDialogAnimationType.SLIDEUP}
              className="uitk-dialog-fullscreen-bg-default"
            >
              <UitkDialogContent className="no-padding">
                <UitkToolbar
                  header={title}
                  iconLabel={formatText("mediaGallery.button.close")}
                  action={closeThumbnailsGallery}
                />
                <UitkMediaGalleryThumbnailView images={galleryImages} onImageSelected={openMediaGallery} />
              </UitkDialogContent>
            </UitkFullscreenDialog>
          </Layer>
        </QueryRoute>
      </div>
    );
  })
);

interface MediaDisplayProps {
  id: string;
  media: Media[];
  title?: string;
}

export const MediaDisplay = ({ id, media, title }: MediaDisplayProps) => {
  if (media.length === 1) {
    const mediaItem = media[0];
    if (mediaItem.mediaType === "Image") {
      return (
        <UitkFigure
          ratio={UitkFigureAspectRatioType.R16_9}
          className="all-image-fit-cover"
          data-testid="galleryCarouselImage"
        >
          <BlossomImage src={mediaItem.mediaUrl} alt={mediaItem.mediaAlt} />
        </UitkFigure>
      );
    }
    return <VideoDisplay id={id} video={mediaItem} />;
  }
  return <MediaCarousel id={id} media={media} title={title || ""} identifier={id} />;
};

export default MediaDisplay;
